/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */
export default class CampaignModel {
  /**
   * Adjusts the time in the operation name if possible, returns the original operation name if not.
   * @param {string} operationName - The operation name containing the time in the format "(DD/MM/YYYY, HH:mm:ss)".
   * @param {string} campaignType - The type of the campaign.
   * @returns {string} - The operation name with the adjusted local time.
   */
  static formatOperationName(operationName, campaignType) {
    if (campaignType !== 'node_update') {
      return operationName || '';
    }
    const version = operationName.split('(')[0].trim();
    const incomingTime = operationName.split('(')[1].split(')')[0];

    const localTime = new Date(incomingTime).toLocaleString('en-GB');
    if (localTime === 'Invalid Date') {
      return operationName;
    }
    return `${version} (${localTime})`;
  }

  static formatDate(timestamp) {
    return timestamp ? new Date(timestamp).toLocaleString('en-GB') : '';
  }

  static formatStatus(status) {
    const normalizedStatus = (status || '').toString().trim().toLowerCase().replace(/_/g, ' ');

    if (!normalizedStatus) {
      return '';
    }

    if (normalizedStatus === 'created') {
      return 'In progress';
    }

    if (normalizedStatus === 'error') {
      return 'Failed';
    }

    if (normalizedStatus === 'completed') {
      return 'Completed';
    }

    return normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1);
  }

  static formatProgress(progress) {
    if (progress === 0 || progress) {
      return parseFloat(progress).toFixed(2);
    }
    return '';
  }

  constructor(data = {}) {
    this.id = data._id || data.id || '';
    this.operationName = CampaignModel.formatOperationName(data.operation_name || '', data.campaignType || data.type);
    this.contentId = data.contentId || '';
    this.type = data.type || '';
    this.status = CampaignModel.formatStatus(data.status);
    this.progress = CampaignModel.formatProgress(data.progress ?? data.campaignOptions?.progress);
    this.started = CampaignModel.formatDate(data.timeOfOperationStart);
    this.finished = CampaignModel.formatDate(data.timeOfOperationFinish);

    // additional fields for workload campaigns
    if (data.type === 'workload' && data.contentInfo) {
      this.contentInfo = {};
      this.contentInfo.contentSubType = data.contentSubtype || data.contentInfo.contentSubType || '';

      this.contentInfo.workloadName = data.contentInfo?.workloadName || '';
      this.contentInfo.versionName = data.contentInfo?.versionName || '';
      this.contentInfo.workloadId = data.contentInfo?.workloadId || '';
      this.contentInfo.versionId = data.contentInfo?.versionId || '';
      this.contentInfo.internalDockerRegistry = data.contentInfo?.internalDockerRegistry || false;
    }
  }
}
